import { normalizeReleaseValue } from "./clientMetadata";

export type AnalyticsSurface =
  | "ios_app"
  | "android_app"
  | "web"
  | "unknown";

export interface OperationsClientActivity {
  surface: AnalyticsSurface;
  appVersion: string;
  webVersion: string;
  plays: number;
  logins: number;
}

export interface OperationsDailyActivity {
  date: string;
  plays: number;
  logins: number;
  activePlayers: number;
  byClient: OperationsClientActivity[];
}

export interface OperationsDashboardData {
  version: 1;
  generatedAt: string;
  today: string;
  filter: {
    surface: AnalyticsSurface | null;
    appVersion: string | null;
    webVersion: string | null;
  };
  availableSurfaces: AnalyticsSurface[];
  availableAppVersions: string[];
  availableWebVersions: string[];
  totalLogins: number;
  loginsToday: number;
  uniqueLoggedInPlayers: number;
  registeredPlayers: number;
  totalPlays: number;
  playsToday: number;
  uniquePlayersWithPlays: number;
  activePlayers: number;
  futurePuzzleDays: number;
  puzzlesThrough: string | null;
  dailyActivity: OperationsDailyActivity[];
}

export interface OperationsDashboardFilter {
  surface: AnalyticsSurface | "all";
  appVersion: string | "all";
  webVersion: string | "all";
}

const SURFACES: AnalyticsSurface[] = [
  "ios_app",
  "android_app",
  "web",
  "unknown",
];

export function normalizeOperationsDashboard(
  value: unknown,
): OperationsDashboardData | null {
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.dailyActivity)) {
    return null;
  }

  const generatedAt = stringField(value.generatedAt);
  const today = stringField(value.today);
  const uniqueLoggedInPlayers = countField(value.uniqueLoggedInPlayers);
  const registeredPlayers = countField(value.registeredPlayers);
  const uniquePlayersWithPlays = countField(value.uniquePlayersWithPlays);
  const activePlayers = countField(value.activePlayers);
  const futurePuzzleDays = countField(value.futurePuzzleDays);
  if (
    generatedAt === null ||
    today === null ||
    uniqueLoggedInPlayers === null ||
    registeredPlayers === null ||
    uniquePlayersWithPlays === null ||
    activePlayers === null ||
    futurePuzzleDays === null
  ) {
    return null;
  }

  const dailyActivity: OperationsDailyActivity[] = [];
  for (const rawDay of value.dailyActivity) {
    const day = normalizeDay(rawDay);
    if (!day) return null;
    dailyActivity.push(day);
  }
  dailyActivity.sort((left, right) => left.date.localeCompare(right.date));

  const availableAppVersions = [
    ...new Set([
      ...normalizeAvailableVersions(value.availableAppVersions),
      ...dailyActivity.flatMap((day) =>
        day.byClient.map((entry) => entry.appVersion),
      ),
      "unknown",
    ]),
  ].sort(compareVersionOption);
  const availableWebVersions = [
    ...new Set([
      ...normalizeAvailableVersions(value.availableWebVersions),
      ...dailyActivity.flatMap((day) =>
        day.byClient.map((entry) => entry.webVersion),
      ),
      "unknown",
    ]),
  ].sort(compareVersionOption);
  const totalPlays = dailyActivity.reduce((sum, day) => sum + day.plays, 0);
  const totalLogins = dailyActivity.reduce((sum, day) => sum + day.logins, 0);
  const todayActivity = dailyActivity.find((day) => day.date === today);

  return {
    version: 1,
    generatedAt,
    today,
    filter: normalizeServerFilter(value.filter),
    availableSurfaces: [...SURFACES],
    availableAppVersions,
    availableWebVersions,
    totalLogins,
    loginsToday: todayActivity?.logins ?? 0,
    uniqueLoggedInPlayers,
    registeredPlayers,
    totalPlays,
    playsToday: todayActivity?.plays ?? 0,
    uniquePlayersWithPlays,
    activePlayers,
    futurePuzzleDays,
    puzzlesThrough:
      value.puzzlesThrough === null
        ? null
        : stringField(value.puzzlesThrough),
    dailyActivity,
  };
}

function normalizeAvailableVersions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) =>
    entry === "unknown" || normalizeReleaseValue(entry)
      ? [String(entry)]
      : [],
  );
}

export function filterOperationsDashboard(
  data: OperationsDashboardData,
  filter: OperationsDashboardFilter,
): OperationsDashboardData {
  const surface = filter.surface === "all" ? null : filter.surface;
  const appVersion = filter.appVersion === "all" ? null : filter.appVersion;
  const webVersion = filter.webVersion === "all" ? null : filter.webVersion;
  const dailyActivity = data.dailyActivity.map((day) => {
    const byClient = day.byClient.filter(
      (entry) =>
        (surface === null || entry.surface === surface) &&
        (appVersion === null || entry.appVersion === appVersion) &&
        (webVersion === null || entry.webVersion === webVersion),
    );
    return {
      ...day,
      plays: sumMetric(byClient, "plays"),
      logins: sumMetric(byClient, "logins"),
      byClient,
    };
  });
  const todayActivity = dailyActivity.find((day) => day.date === data.today);

  return {
    ...data,
    filter: { surface, appVersion, webVersion },
    totalPlays: dailyActivity.reduce((sum, day) => sum + day.plays, 0),
    playsToday: todayActivity?.plays ?? 0,
    totalLogins: dailyActivity.reduce((sum, day) => sum + day.logins, 0),
    loginsToday: todayActivity?.logins ?? 0,
    dailyActivity,
  };
}

function normalizeDay(value: unknown): OperationsDailyActivity | null {
  if (!isRecord(value)) return null;
  const date = stringField(value.date);
  const plays = countField(value.plays);
  const logins = countField(value.logins);
  const activePlayers = countField(value.activePlayers);
  if (
    date === null ||
    plays === null ||
    logins === null ||
    activePlayers === null
  ) {
    return null;
  }

  const byClient: OperationsClientActivity[] = [];
  if (Array.isArray(value.byClient)) {
    for (const row of value.byClient) {
      if (!isRecord(row)) continue;
      const rowPlays = countField(row.plays);
      const rowLogins = countField(row.logins);
      if (rowPlays === null || rowLogins === null) continue;
      mergeActivity(byClient, {
        surface: isAnalyticsSurface(row.surface) ? row.surface : "unknown",
        appVersion:
          row.appVersion === "unknown" || normalizeReleaseValue(row.appVersion)
            ? String(row.appVersion)
            : "unknown",
        webVersion:
          row.webVersion === "unknown" || normalizeReleaseValue(row.webVersion)
            ? String(row.webVersion)
            : "unknown",
        plays: rowPlays,
        logins: rowLogins,
      });
    }
  }

  const representedPlays = sumMetric(byClient, "plays");
  const representedLogins = sumMetric(byClient, "logins");
  if (representedPlays < plays || representedLogins < logins) {
    mergeActivity(byClient, {
      surface: "unknown",
      appVersion: "unknown",
      webVersion: "unknown",
      plays: Math.max(0, plays - representedPlays),
      logins: Math.max(0, logins - representedLogins),
    });
  }
  byClient.sort(compareActivity);

  return {
    date,
    plays: sumMetric(byClient, "plays"),
    logins: sumMetric(byClient, "logins"),
    activePlayers,
    byClient,
  };
}

function normalizeServerFilter(value: unknown): OperationsDashboardData["filter"] {
  if (!isRecord(value)) {
    return { surface: null, appVersion: null, webVersion: null };
  }
  return {
    surface: isAnalyticsSurface(value.surface) ? value.surface : null,
    appVersion:
      value.appVersion === "unknown" || normalizeReleaseValue(value.appVersion)
        ? String(value.appVersion)
        : null,
    webVersion:
      value.webVersion === "unknown" || normalizeReleaseValue(value.webVersion)
        ? String(value.webVersion)
        : null,
  };
}

function mergeActivity(
  rows: OperationsClientActivity[],
  incoming: OperationsClientActivity,
): void {
  const existing = rows.find(
    (row) =>
      row.surface === incoming.surface &&
      row.appVersion === incoming.appVersion &&
      row.webVersion === incoming.webVersion,
  );
  if (existing) {
    existing.plays += incoming.plays;
    existing.logins += incoming.logins;
  } else if (incoming.plays > 0 || incoming.logins > 0) {
    rows.push({ ...incoming });
  }
}

function sumMetric(
  rows: readonly OperationsClientActivity[],
  metric: "plays" | "logins",
): number {
  return rows.reduce((sum, row) => sum + row[metric], 0);
}

function isAnalyticsSurface(value: unknown): value is AnalyticsSurface {
  return typeof value === "string" && SURFACES.includes(value as AnalyticsSurface);
}

function countField(value: unknown): number | null {
  return Number.isSafeInteger(value) && Number(value) >= 0
    ? Number(value)
    : null;
}

function stringField(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function compareActivity(
  left: OperationsClientActivity,
  right: OperationsClientActivity,
): number {
  return (
    SURFACES.indexOf(left.surface) - SURFACES.indexOf(right.surface) ||
    left.appVersion.localeCompare(right.appVersion) ||
    left.webVersion.localeCompare(right.webVersion)
  );
}

function compareVersionOption(left: string, right: string): number {
  return left === "unknown"
    ? 1
    : right === "unknown"
      ? -1
      : left.localeCompare(right);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
